'use client'

import { motion } from 'framer-motion'
import { Heart, Lightbulb, Users, Smile } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface BenefitCardProps {
  Icon: LucideIcon
  title: string
  description: string
}

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
}

const BenefitCard: React.FC<BenefitCardProps> = ({
  Icon,
  title,
  description,
}) => (
  <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md transition-transform duration-300 hover:-translate-y-2 h-full">
    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
      <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
      {title}
    </h3>
    <p className="text-sm sm:text-base text-gray-700">{description}</p>
  </div>
)

const TherapyBenefits: React.FC = () => {
  const benefits = [
    {
      Icon: Heart,
      title: 'Develop Healthy Coping Skills',
      description:
        'Learn effective strategies to manage stress, anxiety, depression, and other emotional challenges in your daily life.',
    },
    {
      Icon: Lightbulb,
      title: 'Gain Deeper Self-Understanding',
      description:
        'Explore your patterns of thought, emotion, and behavior to gain valuable insights into yourself and your relationships.',
    },
    {
      Icon: Users,
      title: 'Improve Relationships',
      description:
        'Enhance your communication skills, set healthy boundaries, and foster more fulfilling connections with others.',
    },
    {
      Icon: Smile,
      title: 'Enhance Overall Well‑being',
      description:
        'Work towards greater emotional balance, increased self‑esteem, a stronger sense of purpose, and improved quality of life.',
    },
  ]

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-10 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeIn}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4">
            How Individual Therapy Can Help You Thrive
          </h2>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          {benefits.map(({ Icon, title, description }) => (
            <motion.div key={title} variants={fadeIn}>
              <BenefitCard
                Icon={Icon}
                title={title}
                description={description}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default TherapyBenefits
